/**
 * Connect partner onboarding wizard.
 *
 * Five steps, each committing to the backend as you go (no giant final submit):
 *   1. Org basics (name, type; pricing bucket + freight basis for wholesalers) -> POST org
 *   2. Sage customer mappings (wholesaler) or broker codes (broker)          -> PUT
 *   3. Optional first agreement (title / type / signer)                       -> POST (skippable)
 *   4. Invite first user (email / name / role, org-admin default)             -> POST user
 *   5. Done: summary + link to the new partner's detail page
 *
 * Because each step commits, the org exists after step 1; closing early leaves
 * a valid partner in the directory.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Loader2 } from 'lucide-react';
import {
  useCreateConnectOrg,
  usePutOrgCustomers,
  usePutOrgSalespersons,
  useCreateConnectAgreement,
  useCreateConnectUser,
  type ConnectOrg,
  type OrgType,
  type PricingBucket,
  type FreightBasis,
  type SageCustomerRef,
  type SalespersonCodeRef,
} from '@/lib/useConnect';
import { Modal, inputCls, labelCls, primaryBtnCls, secondaryBtnCls } from './connectShared';
import { CustomerMappingEditor, SalespersonCodeEditor } from './mappingEditors';

type Step = 1 | 2 | 3 | 4 | 5;

export function OnboardingWizard({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>(1);
  const [org, setOrg] = useState<ConnectOrg | null>(null);

  // Step 1 form
  const [name, setName] = useState('');
  const [orgType, setOrgType] = useState<OrgType>('wholesaler');
  const [pricingBucket, setPricingBucket] = useState<PricingBucket>('wholesale');
  const [freightBasis, setFreightBasis] = useState<FreightBasis>('fob');

  // Step 2 drafts
  const [customers, setCustomers] = useState<SageCustomerRef[]>([]);
  const [codes, setCodes] = useState<SalespersonCodeRef[]>([]);

  // Step 3 agreement
  const [agTitle, setAgTitle] = useState('');
  const [agType, setAgType] = useState('');
  const [agSigner, setAgSigner] = useState('');

  // Step 4 user
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [userRole, setUserRole] = useState('org-admin');

  const createOrg = useCreateConnectOrg();
  const putCustomers = usePutOrgCustomers(org?.id);
  const putSalespersons = usePutOrgSalespersons(org?.id);
  const createAgreement = useCreateConnectAgreement();
  const createUser = useCreateConnectUser(org?.id);

  const busy =
    createOrg.isPending ||
    putCustomers.isPending ||
    putSalespersons.isPending ||
    createAgreement.isPending ||
    createUser.isPending;

  const submitStep1 = async () => {
    const created = await createOrg.mutateAsync({
      name: name.trim(),
      org_type: orgType,
      ...(orgType === 'wholesaler'
        ? { pricing_bucket: pricingBucket, freight_basis: freightBasis }
        : {}),
    });
    setOrg(created);
    setStep(2);
  };

  const submitStep2 = async () => {
    if (orgType === 'broker') {
      await putSalespersons.mutateAsync({
        codes: codes.map((c) => ({
          division_no: c.division_no || undefined,
          salesperson_no: c.salesperson_no,
          commission_rate: c.commission_rate ?? undefined,
        })),
      });
    } else {
      await putCustomers.mutateAsync({
        customers: customers.map((c) => ({
          division_no: c.division_no || undefined,
          customer_no: c.customer_no,
          is_primary: c.is_primary,
        })),
      });
    }
    setStep(3);
  };

  const submitStep3 = async () => {
    if (!org) return;
    if (agTitle.trim()) {
      await createAgreement.mutateAsync({
        org_id: org.id,
        title: agTitle.trim(),
        agreement_type: agType.trim() || undefined,
        signer_email: agSigner.trim() || undefined,
      });
    }
    setStep(4);
  };

  const submitStep4 = async () => {
    if (userEmail.trim()) {
      await createUser.mutateAsync({
        email: userEmail.trim(),
        name: userName.trim() || undefined,
        role: userRole,
      });
    }
    setStep(5);
  };

  const mutationError =
    createOrg.error ||
    putCustomers.error ||
    putSalespersons.error ||
    createAgreement.error ||
    createUser.error;

  const title = `New partner (step ${step} of 5)`;

  return (
    <Modal
      title={title}
      onClose={onClose}
      wide
      footer={
        step === 5 ? (
          <>
            <button type="button" onClick={onClose} className={secondaryBtnCls}>Close</button>
            {org && (
              <button
                type="button"
                onClick={() => navigate(`/connect/partners/${org.id}`)}
                className={primaryBtnCls}
              >
                Open partner
              </button>
            )}
          </>
        ) : (
          <WizardFooter
            step={step}
            busy={busy}
            canAdvance={step === 1 ? name.trim().length > 0 : true}
            onBack={step > 1 ? () => setStep((s) => (s - 1) as Step) : undefined}
            onSkip={step === 3 ? () => setStep(4) : undefined}
            onNext={
              step === 1 ? submitStep1 : step === 2 ? submitStep2 : step === 3 ? submitStep3 : submitStep4
            }
          />
        )
      }
    >
      {mutationError && (
        <p className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300">
          {mutationError.message}
        </p>
      )}

      {step === 1 && (
        <div className="space-y-3">
          <div>
            <label className={labelCls}>Partner name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="Acme Distribution Co." />
          </div>
          <div>
            <label className={labelCls}>Type</label>
            <select value={orgType} onChange={(e) => setOrgType(e.target.value as OrgType)} className={inputCls}>
              <option value="wholesaler">Wholesaler</option>
              <option value="broker">Broker</option>
            </select>
          </div>
          {orgType === 'wholesaler' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Pricing bucket</label>
                <select value={pricingBucket} onChange={(e) => setPricingBucket(e.target.value as PricingBucket)} className={inputCls}>
                  <option value="wholesale">Wholesale</option>
                  <option value="distributor">Distributor</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Freight basis</label>
                <select value={freightBasis} onChange={(e) => setFreightBasis(e.target.value as FreightBasis)} className={inputCls}>
                  <option value="fob">FOB</option>
                  <option value="delivered">Delivered</option>
                </select>
              </div>
            </div>
          )}
        </div>
      )}

      {step === 2 && (
        <div>
          {orgType === 'broker' ? (
            <>
              <p className="mb-2 text-sm text-amp-ink dark:text-amp-dark-ink">Map the Sage salesperson codes this broker represents.</p>
              <SalespersonCodeEditor value={codes} onChange={setCodes} />
            </>
          ) : (
            <>
              <p className="mb-2 text-sm text-amp-ink dark:text-amp-dark-ink">Map the Sage customer accounts this partner orders under.</p>
              <CustomerMappingEditor value={customers} onChange={setCustomers} />
            </>
          )}
        </div>
      )}

      {step === 3 && (
        <div className="space-y-3">
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">Optional. Create a first agreement now, or skip and add one later.</p>
          <div>
            <label className={labelCls}>Title</label>
            <input value={agTitle} onChange={(e) => setAgTitle(e.target.value)} className={inputCls} placeholder="Wholesale terms 2026" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Type</label>
              <input value={agType} onChange={(e) => setAgType(e.target.value)} className={inputCls} placeholder="terms" />
            </div>
            <div>
              <label className={labelCls}>Signer email</label>
              <input value={agSigner} onChange={(e) => setAgSigner(e.target.value)} className={inputCls} placeholder="buyer@partner.com" />
            </div>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="space-y-3">
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">Invite the first user. A temporary password is emailed to them. Leave blank to skip.</p>
          <div>
            <label className={labelCls}>Email</label>
            <input value={userEmail} onChange={(e) => setUserEmail(e.target.value)} className={inputCls} placeholder="buyer@partner.com" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Name</label>
              <input value={userName} onChange={(e) => setUserName(e.target.value)} className={inputCls} placeholder="Jane Buyer" />
            </div>
            <div>
              <label className={labelCls}>Role</label>
              <select value={userRole} onChange={(e) => setUserRole(e.target.value)} className={inputCls}>
                <option value="org-admin">Org admin</option>
                <option value="buyer">Buyer</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {step === 5 && (
        <div className="space-y-3 text-sm text-amp-ink dark:text-amp-dark-ink">
          <div className="inline-flex items-center gap-2 text-green-700 dark:text-green-300">
            <Check className="h-4 w-4" /> Partner created.
          </div>
          <ul className="space-y-1 text-amp-ink-muted dark:text-amp-dark-ink-muted">
            <li>Name: <span className="text-amp-ink dark:text-amp-dark-ink">{org?.name}</span></li>
            <li>Type: <span className="text-amp-ink dark:text-amp-dark-ink">{orgType}</span></li>
            {orgType === 'broker'
              ? <li>Salesperson codes: <span className="text-amp-ink dark:text-amp-dark-ink">{codes.length}</span></li>
              : <li>Sage customers mapped: <span className="text-amp-ink dark:text-amp-dark-ink">{customers.length}</span></li>}
            <li>First agreement: <span className="text-amp-ink dark:text-amp-dark-ink">{agTitle.trim() ? agTitle : 'skipped'}</span></li>
            <li>First user: <span className="text-amp-ink dark:text-amp-dark-ink">{userEmail.trim() ? userEmail : 'skipped'}</span></li>
          </ul>
        </div>
      )}
    </Modal>
  );
}

function WizardFooter({
  step,
  busy,
  canAdvance,
  onBack,
  onSkip,
  onNext,
}: {
  step: Step;
  busy: boolean;
  canAdvance: boolean;
  onBack?: () => void;
  onSkip?: () => void;
  onNext: () => void;
}) {
  return (
    <>
      {onBack && (
        <button type="button" onClick={onBack} disabled={busy} className={secondaryBtnCls}>Back</button>
      )}
      {onSkip && (
        <button type="button" onClick={onSkip} disabled={busy} className={secondaryBtnCls}>Skip</button>
      )}
      <button type="button" onClick={onNext} disabled={busy || !canAdvance} className={primaryBtnCls}>
        {busy && <Loader2 className="h-3 w-3 animate-spin" />}
        {step === 4 ? 'Finish' : 'Next'}
      </button>
    </>
  );
}
